import axios from "axios";
import type { AppDispatch } from "../../../common/state/store";
import apiUrls from "../../../api";
import { setMediaPushJob, updateMediaPushStatus } from "../state/deviceInstallerSlice";
import type { MediaManifestItem, MediaPushMode, MediaPushTarget } from "./mediaFiles";
import { pushMediasToDevice } from "./adbMediaPusher";
import { pushMediasViaAgent } from "./agentMediaPusher";
import { isAbortError } from "./adbHelpers";

export type { MediaPushMode, MediaPushTarget } from "./mediaFiles";

export interface ManifestModule {
  id: number;
  name: string;
  medias: MediaManifestItem[];
}

export interface MediaManifest {
  projectGroupId: number;
  projectGroupName: string;
  modules: ManifestModule[];
}

let currentAbortController: AbortController | null = null;

export function cancelCurrentMediaPush(): void {
  if (currentAbortController) {
    currentAbortController.abort();
    currentAbortController = null;
  }
}

/** Fetch the media manifest (module list) without starting the push */
export async function fetchMediaManifest(projectGroupId: number): Promise<MediaManifest> {
  const response = await axios.get<MediaManifest>(
    `${apiUrls.stats.projectGroupMediaManifest}?projectGroupId=${projectGroupId}`,
  );
  return response.data;
}

/** Push medias for the selected modules to the device or agent */
export async function startMediaPush(
  projectGroupId: number,
  packageName: string,
  selectedModules: ManifestModule[],
  dispatch: AppDispatch,
  options?: { mode?: MediaPushMode; appId?: string; target?: MediaPushTarget },
): Promise<void> {
  const abortController = new AbortController();
  currentAbortController = abortController;
  const { signal } = abortController;

  const seen = new Set<number>();
  const medias: MediaManifestItem[] = [];
  for (const mod of selectedModules) {
    for (const m of mod.medias) {
      if (!seen.has(m.id)) {
        seen.add(m.id);
        medias.push(m);
      }
    }
  }

  dispatch(
    setMediaPushJob({
      projectGroupId,
      packageName,
      status: "pushing",
      totalFiles: medias.length,
      pushedFiles: 0,
      failedFiles: 0,
      skippedFiles: 0,
      currentFileName: "",
      currentFileProgress: 0,
    }),
  );

  try {
    if (medias.length === 0) {
      dispatch(updateMediaPushStatus({ status: "success" }));
      return;
    }

    if (signal.aborted) throw new DOMException("Cancelled", "AbortError");

    const mode = options?.mode ?? "adb";
    const result =
      mode === "agent" && options?.appId
        ? await pushMediasViaAgent(options.appId, medias, dispatch, signal)
        : await pushMediasToDevice(
            packageName,
            medias,
            dispatch,
            signal,
            options?.target ?? "android",
          );

    dispatch(
      updateMediaPushStatus({
        status: "success",
        failedFiles: result.failed,
        skippedFiles: result.skipped,
      }),
    );
  } catch (err: unknown) {
    if (isAbortError(err)) {
      dispatch(updateMediaPushStatus({ status: "cancelled" }));
    } else {
      const message = err instanceof Error ? err.message : String(err);
      dispatch(
        updateMediaPushStatus({
          status: "error",
          errorMessage: message,
        }),
      );
    }
  } finally {
    currentAbortController = null;
  }
}
